const ApiResolvers = require('@/lib/Api/ApiResolvers');
const { generateVersionString } = require('@/lib/Application/versionString');

/**
 * @constructor
 *
 * @param {AppConfig} appConfig
 * @param {CurrentDevice} currentDevice
 * @param {UserService} userService
 */
const ApiRequestParams = function (
    appConfig,
    currentDevice,
    userService
) {
    this.appConfig = appConfig;
    this.currentDevice = currentDevice;
    this.userService = userService;
};

ApiRequestParams.prototype = {
    /**
     * Returns the default parameters that should be added to every API request.
     * These are functions so angular-resource will call the function each time to get the actual value.
     *
     * @return {{apiKey: (function(): *), branchID: (function(): *), deviceGuid: (function(): *), requestTradingSessionID: (function(): *), isoDates: number }}
     */
    defaults() {
        const self = this;

        // These are returned as functions so they are invoked
        // for each request and the latest value is sent.
        // Otherwise we might be using stale data.
        let params = {
            apiKey: function apiKey() {
                return self.userService.getApiKey();
            },
            applicationVersion: generateVersionString(),
            branchID: function branchID() {
                return self.userService.getOrderBranchID();
            },
            deviceGuid: function () {
                return self.currentDevice.getDeviceGuid();
            },
            requestTradingSessionID: function () {
                return self.currentDevice.getTradingSessionID();
            },
            // Tell API to return dates in ISO-8601 (where implemented).
            isoDates: 1,

            /**
             * Send the salesChannelID/paymentChannelID with all requests.
             */
            salesChannelID: this.appConfig.channelID,
        };
        if (self.appConfig.isBeta) {
            params.isBeta = 1;
        }
        return params;
    },

    /**
     * The 'defaults' method provides the default values as functions, which is what angular-resource wants.
     * This method returns the actual values as a flat object of string/number values.
     *
     * @return {*}
     */
    getValues() {
        const values = this.defaults();

        Object.keys(values).forEach((key) => {
            if (typeof values[key] === 'function') {
                values[key] = values[key]();
            }
        });

        return values;
    },

    defaultExcludeFields() {
        return [
            'categories.buttonText',
            'categories.description',
            'categories.featuredImage',
            'categories.icon',
            'categories.latLng',
            'categories.shortDescription',
            'categories.terms',
        ].join(',');
    },

    defaultMappers() {
        return {
            'mappers[0][normalise]': 'true',
            'mappers[1][toTable]': 'true',
        };
    },

    /**
     * Returns a string of resolve parameters for requests to load a specific order.
     * Note: This is also used for some search requests too that have not been updated.
     *
     * Here for consistency because it's used in a few places.
     * Having other resolvers in addition to '*' looks strange but is for the following reasons:
     * - 'seller' returns the same data as the old 'sellers' included in '*' but renamed.
     * - 'orderBranch' is only returned when specifically requested to reduce the response size for non ProPoint users.
     *
     * @return {string[]}
     */
    getResolveForOrderRequest() {
        return [
            '*',
            'seller',
            'orderBranch',
            'bookinggroups',
            'orderItems.optionSets',
            ApiResolvers.ORDER_DOCUMENT_NUMBERS,
            ApiResolvers.ORDER_FULFILLMENT_INSTRUCTIONS,
        ];
    },

    /**
     * Returns a string of resolve parameters for requests to return a list of orders.
     *
     * @return {string[]}
     */
    getResolveForOrderSearchRequest() {
        // This no longer uses * because we don't need all that data for the list view.
        // It used to be that the orders loaded here were the same data displayed on the details page,
        // but now the details page loads the data it needs separately. So now we only need to load what
        // is needed for the list, which can be significantaly smaller.
        return [
            // To show fulfilled/unfulfilled status in the list:
            'orderitems.items', // To determine if an item is a kitchen item.
            'fulfillments',

            // To show the redeem button on lines when a specific session was searched for:
            'orderitems.iteminstances',

            // To show branch name for cross-branch orders in the list:
            'orderBranch',

            // To show membership details in the list:
            'orderitems.iteminstances.membershipsubscriptions.memberships',
            'orderitems.iteminstances.membershipsubscriptions.memberships.members.contacts',

            // To show the overpaid / underpaid status in the list:
            'paymentlines',
            // To see if a payment is a (pending) GoCardless payment to show the appropriate status label:
            'paymentlines.paymentmethods',

            // Resolve document numbers so we can show the order collection numbers:
            ApiResolvers.ORDER_DOCUMENT_NUMBERS,
        ];
    },

    /**
     * Returns an array of all branchIDs the user has access to, with the first entry being the current branch.
     *
     * @return {number[]}
     */
    getAllUserBranchIDs() {
        const branchIDs = [];

        const currentBranchID = this.userService.currentBranch ? this.userService.currentBranch.getID() : null;
        if (currentBranchID) {
            branchIDs.push(currentBranchID);
        }

        this.userService.branches
            .map((branch) => branch.getID())
            .filter((branchID) => !currentBranchID || branchID !== currentBranchID)
            .forEach((branchID) => {
                branchIDs.push(branchID);
            });

        return branchIDs;
    },
};

module.exports = ApiRequestParams;
